import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Alert, Empty, Space, Badge, Row, Col, Flex } from "antd";
import { useTranslation } from "react-i18next";

import Page from "../../shared/components/layout/Page";
import ItemCard from "../../shared/components/items/ItemCard";
import ClearAllButton from "../../shared/components/buttons/ClearAllButton";
import { ItemAttributes } from "../../types/model";
import { useAntdTheme, useReconnectingWebSocket } from "../../shared/hooks";

import { WS_URL, getWorkstationTranslationKey } from "../../shared/utils/Util";
import notificationSound from "../../assets/notification.mp3";

type ItemByPriorityInfo = {
  item_attributes: ItemAttributes;
  count: number;
  oldest_pending_time: string | null;
  newest_pending_time: string | null;
};

type ItemsByPriority = [number, ItemByPriorityInfo[]];

type DeltaAttrInfo = {
  count_by_priority: { [priority: string]: number };
  oldest_pending_time_by_priority: { [priority: string]: string };
  newest_pending_time_by_priority?: { [priority: string]: string };
  item_attributes: ItemAttributes;
};

type Delta = {
  [attrID: string]: DeltaAttrInfo;
};

const notificationAudio = new Audio(notificationSound);

const WebsocketDisconnectBanner: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Alert message={t("unable_to_connect")} description={t("retrying")} type="error" showIcon />
  );
};

const updateItemsByPriority = (oldState: any, delta: Delta) => {
  const newState = { ...oldState };

  Object.entries(delta).forEach(([attrID, attrInfo]: [string, DeltaAttrInfo]) => {
    // Delete wherever this item attribute ID appears across all priorities
    Object.keys(newState).forEach((priority) => {
      newState[priority] = { ...newState[priority] };
      if (newState[priority][attrID]) {
        delete newState[priority][attrID];
      }
    });

    // Update state according to new counts for this item attribute ID
    Object.entries(attrInfo.count_by_priority).forEach(([priority, count]) => {
      if (!newState[priority]) {
        newState[priority] = {};
      }
      let oldestPendingTime = null;
      if (attrInfo.oldest_pending_time_by_priority[priority]) {
        oldestPendingTime = attrInfo.oldest_pending_time_by_priority[priority];
      }

      let newestPendingTime = null;
      if (
        attrInfo.newest_pending_time_by_priority &&
        attrInfo.newest_pending_time_by_priority[priority]
      ) {
        newestPendingTime = attrInfo.newest_pending_time_by_priority[priority];
      }

      newState[priority][attrID] = {
        item_attributes: attrInfo.item_attributes,
        count: count,
        oldest_pending_time: oldestPendingTime,
        newest_pending_time: newestPendingTime,
      };
    });

    // Delete any priorities with no items
    Object.keys(newState).forEach((priority) => {
      if (Object.keys(newState[priority]).length === 0) {
        delete newState[priority];
      }
    });
  });

  // Play notification sound if new actions showed up for the workstation.
  if (Object.keys(oldState).length === 0 && Object.keys(newState).length > 0) {
    const Android = window.Android;
    if (Android) {
      Android.playSound();
    } else {
      notificationAudio.play().catch((err) => console.error(err));
    }
  }

  return newState;
};

const Component: React.FC<any> = () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { state } = useLocation();

  if (!state || !state.workStationName || !state.workStationID) {
    window.location.href = "/";
    return;
  }
  const wsID = state.workStationID;
  const wsName = state.workStationName;

  // Translate workstation name
  const translationKey = getWorkstationTranslationKey(wsName);
  const displayName = translationKey.startsWith("workstation_") ? t(translationKey) : wsName;

  const restaurantId = sessionStorage.getItem("restaurant_id");
  if (!restaurantId) {
    const location = useLocation();
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // Object mapping priority to item attributes to item
  const [itemsByPriority, setItemsByPriority] = useState({});

  const { disconnected: websocketDisconnect } = useReconnectingWebSocket({
    url: `${WS_URL}/v1/ws/r/${restaurantId}/workstations/${wsID}/queue`,
    onOpen: () => {
      // Clear state since the backend only returns the latest state, and our
      // local state could be very stale if we were disconnected.
      setItemsByPriority({});
    },
    onMessage: (message) => {
      setItemsByPriority((prev) => updateItemsByPriority(prev, message));
    },
  });

  let itemsByPriorityArr = Object.entries(itemsByPriority);
  itemsByPriorityArr.sort((item1, item2) => {
    const [priority1] = item1;
    const [priority2] = item2;
    return -1 * (Number(priority1) - Number(priority2));
  });
  const typedItemsByPriorityArr: ItemsByPriority[] = itemsByPriorityArr.map(
    ([priority, unsortedItems]) => {
      const items: ItemByPriorityInfo[] = Object.values(unsortedItems);
      items.sort((item1, item2) => {
        if (item1.oldest_pending_time !== item2.oldest_pending_time) {
          return (
            Date.parse(item1.oldest_pending_time || "0") -
            Date.parse(item2.oldest_pending_time || "0")
          );
        }
        return item1.item_attributes.id.localeCompare(item2.item_attributes.id);
      });

      return [Number(priority), items];
    }
  );

  // Count total items
  const totalItems = typedItemsByPriorityArr.reduce((sum, [_, items]) => sum + items.length, 0);

  return (
    <Page title={displayName} noCard={true}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Fixed Action Bar */}
        <div
          style={{
            position: "sticky",
            top: 64,
            zIndex: 100,
            background: token.colorBgContainer,
            borderBottom: `2px solid ${token.colorBorderSecondary}`,
            padding: `${token.paddingMD}px ${token.paddingLG}px`,
            boxShadow: token.boxShadowSecondary,
          }}
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
            <Badge
              count={totalItems}
              showZero
              color={token.colorPrimary}
              style={{ fontSize: token.fontSizeLG }}
            >
              <div
                style={{
                  fontSize: token.fontSizeHeading4,
                  fontWeight: token.fontWeightStrong,
                  paddingRight: token.paddingMD,
                }}
              >
                {t("items") || "Items"}
              </div>
            </Badge>
            <Space size="middle">
              <ClearAllButton
                itemsByPriorityArr={typedItemsByPriorityArr}
                restaurantId={restaurantId}
                workstationID={wsID}
              />
            </Space>
          </Flex>
        </div>

        {/* Websocket Disconnect Banner */}
        {websocketDisconnect && <WebsocketDisconnectBanner />}

        {/* Items Grid with Responsive Breakpoints */}
        {typedItemsByPriorityArr.length > 0 ? (
          <Row gutter={[16, 16]} style={{ padding: `0 ${token.paddingLG}px` }}>
            {typedItemsByPriorityArr.map(([priority, items]) =>
              items.map((item) => (
                <Col
                  key={`${priority}:${item.item_attributes.id}`}
                  xs={24}
                  sm={12}
                  md={8}
                  lg={6}
                  xl={6}
                  xxl={4}
                >
                  <ItemCard
                    priority={priority}
                    item={item}
                    showBigFont={true}
                    restaurantId={restaurantId}
                    workstationID={wsID}
                  />
                </Col>
              ))
            )}
          </Row>
        ) : (
          <Flex justify="center" align="center" style={{ padding: token.paddingXL }}>
            <Empty description={t("no_items") || "No items"} />
          </Flex>
        )}
      </Space>
    </Page>
  );
};

export default Component;
